import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertCircle,
  AlertTriangle, 
  Info, 
  Terminal, 
  Bug, 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  Clock,
  Archive,
  Loader2,
  MoreHorizontal,
  CheckCircle,
  Eye,
  Pencil,
  Zap
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { createLogger, LogLevel, LogSource } from "@/lib/utils/logger";

type SystemLog = {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info' | 'debug';
  source: string;
  message: string;
  details: string | null;
  component: string | null;
  status: 'unresolved' | 'investigating' | 'resolved' | 'archived';
}

type LogCounts = {
  error: number;
  warning: number;
  info: number;
  debug: number;
}

export default function TechnicalLogs() {
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("application");
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isTestLoggerDialogOpen, setIsTestLoggerDialogOpen] = useState(false);
  const [testLogSource, setTestLogSource] = useState<LogSource>("frontend");
  const [testLogLevel, setTestLogLevel] = useState<LogLevel>("info");
  const [testLogMessage, setTestLogMessage] = useState("");
  const [testLogComponent, setTestLogComponent] = useState("");
  const [testLogDetails, setTestLogDetails] = useState("");

  const queryClient = useQueryClient();

  const { 
    data: allLogs,
    isLoading: isLoadingLogs,
    refetch: refetchLogs
  } = useQuery({
    queryKey: ['system-logs', activeTab],
    queryFn: async () => {
      let query = supabase
        .from('system_logs')
        .select('*')
        .not('status', 'eq', 'archived'); 
        
      if (activeTab !== 'application') {
        query = query.eq('source', activeTab);
      }
      
      const { data, error } = await query.order('timestamp', { ascending: false });
        
      if (error) {
        console.error("Error fetching logs:", error);
        toast({
          title: "Error loading logs",
          description: error.message,
          variant: "destructive"
        });
        throw new Error(error.message);
      }
      
      return data as SystemLog[];
    }
  });

  const { 
    data: logCounts,
    isLoading: isLoadingCounts,
    refetch: refetchCounts
  } = useQuery({
    queryKey: ['log-counts'],
    queryFn: async () => {
      const counts: LogCounts = {
        error: 0,
        warning: 0,
        info: 0,
        debug: 0
      };
      
      const { data: errorData, error: errorError } = await supabase
        .from('system_logs')
        .select('id')
        .eq('level', 'error')
        .not('status', 'eq', 'archived');
        
      if (!errorError && errorData) {
        counts.error = errorData.length;
      }
      
      const { data: warningData, error: warningError } = await supabase
        .from('system_logs')
        .select('id')
        .eq('level', 'warning')
        .not('status', 'eq', 'archived');
        
      if (!warningError && warningData) {
        counts.warning = warningData.length;
      }
      
      const { data: infoData, error: infoError } = await supabase
        .from('system_logs')
        .select('id')
        .eq('level', 'info')
        .not('status', 'eq', 'archived');
        
      if (!infoError && infoData) {
        counts.info = infoData.length;
      }
      
      const { data: debugData, error: debugError } = await supabase
        .from('system_logs')
        .select('id')
        .eq('level', 'debug')
        .not('status', 'eq', 'archived');
        
      if (!debugError && debugData) {
        counts.debug = debugData.length;
      }
      
      return counts;
    }
  });

  useEffect(() => {
    const channel = supabase
      .channel('system-logs-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_logs',
        },
        (payload) => {
          console.log('New log received:', payload);
          
          queryClient.invalidateQueries({ queryKey: ['system-logs'] });
          queryClient.invalidateQueries({ queryKey: ['log-counts'] });
          
          const newLog = payload.new as SystemLog;
          const variant = newLog.level === 'error' ? 'destructive' : 
                          newLog.level === 'warning' ? 'warning' : 'default';
          
          toast({
            title: `New ${newLog.level.toUpperCase()} Log`,
            description: newLog.message,
            variant: variant as any,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const createTestLogMutation = useMutation({
    mutationFn: async (logData: {
      level: LogLevel;
      source: LogSource;
      message: string;
      component?: string;
      details?: string;
    }) => {
      const logger = createLogger(logData.source, logData.component || null);
      
      switch(logData.level) {
        case 'error':
          await logger.error(logData.message, logData.details);
          break;
        case 'warning':
          await logger.warning(logData.message, logData.details);
          break;
        case 'info':
          await logger.info(logData.message, logData.details);
          break;
        case 'debug':
          await logger.debug(logData.message, logData.details);
          break;
      }
      
      return true;
    },
    onSuccess: () => {
      toast({
        title: "Test log created",
        description: "The log has been added to the system.",
      });
      setIsTestLoggerDialogOpen(false);
      setTestLogMessage("");
      setTestLogDetails("");
      
      setTimeout(() => {
        refetchLogs();
        refetchCounts();
      }, 1000);
    },
    onError: (error) => {
      toast({
        title: "Error creating log",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const archiveMutation = useMutation({
    mutationFn: async (logsToArchive: SystemLog[]) => {
      if (!logsToArchive || logsToArchive.length === 0) {
        throw new Error("No logs to archive");
      }

      const logIds = logsToArchive.map(log => log.id);
      
      const { error } = await supabase
        .from('system_logs')
        .update({ status: 'archived' })
        .in('id', logIds);
        
      if (error) {
        console.error("Error archiving logs:", error);
        throw new Error(error.message);
      }

      return logIds.length;
    },
    onSuccess: (count) => {
      toast({
        title: "Logs archived",
        description: `Successfully archived ${count} logs.`,
      });
      refetchLogs();
      refetchCounts();
    },
    onError: (error) => {
      console.error("Error in archive mutation:", error);
      toast({
        title: "Error archiving logs",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ logId, newStatus }: { logId: string, newStatus: 'unresolved' | 'investigating' | 'resolved' }) => {
      const { error } = await supabase
        .from('system_logs')
        .update({ status: newStatus })
        .eq('id', logId);
        
      if (error) {
        console.error("Error updating log status:", error);
        throw new Error(error.message);
      }

      return { logId, newStatus };
    },
    onSuccess: ({ logId, newStatus }) => {
      toast({
        title: "Status updated",
        description: `Log has been marked as ${newStatus}.`,
      });
      refetchLogs();
      refetchCounts();
    },
    onError: (error) => {
      console.error("Error in update status mutation:", error);
      toast({
        title: "Error updating status",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const getFilteredLogs = () => {
    if (!allLogs) return [];
    
    return allLogs.filter(log => {
      const matchesSearch = 
        log.message.toLowerCase().includes(searchQuery.toLowerCase()) || 
        log.id.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesLevel = levelFilter === "all" || log.level === levelFilter;
      const matchesSource = sourceFilter === "all" || log.source === sourceFilter;
      
      return matchesSearch && matchesLevel && matchesSource;
    });
  };

  const filteredLogs = getFilteredLogs();

  const handleRefresh = async () => {
    toast({
      title: "Refreshing data",
      description: "Fetching latest logs...",
    });
    
    await Promise.all([
      refetchLogs(),
      refetchCounts()
    ]);
  };

  const handleArchiveLogs = async () => {
    try {
      setIsArchiveDialogOpen(false);
      archiveMutation.mutate(filteredLogs);
    } catch (error) {
      console.error("Error archiving logs:", error);
      toast({
        title: "Error archiving logs",
        description: "An error occurred while attempting to archive logs.",
        variant: "destructive"
      });
    }
  };

  const handleUpdateStatus = async (logId: string, newStatus: 'unresolved' | 'investigating' | 'resolved') => {
    try {
      updateStatusMutation.mutate({ logId, newStatus });
    } catch (error) {
      console.error("Error updating log status:", error);
      toast({
        title: "Error updating status",
        description: "An error occurred while updating log status.",
        variant: "destructive"
      });
    }
  };

  const handleViewDetails = (logId: string) => {
    setSelectedLogId(logId);
    setIsDetailsDialogOpen(true);
  };

  const getSelectedLog = () => {
    if (!selectedLogId || !allLogs) return null;
    return allLogs.find(log => log.id === selectedLogId) || null;
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case "error":
        return <Badge variant="destructive" className="font-subtitle">Error</Badge>;
      case "warning":
        return <Badge variant="warning" className="bg-amber-500 font-subtitle">Warning</Badge>;
      case "info":
        return <Badge variant="accent" className="font-subtitle">Info</Badge>;
      case "debug":
        return <Badge variant="secondary" className="font-subtitle">Debug</Badge>;
      default:
        return <Badge className="font-subtitle">{level}</Badge>;
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case "api":
        return <Badge variant="outline" className="bg-accent/10 text-accent">API</Badge>;
      case "database":
        return <Badge variant="outline" className="bg-primary/10 text-primary">Database</Badge>;
      case "auth":
        return <Badge variant="outline" className="bg-violet-500/10 text-violet-500">Auth</Badge>;
      case "frontend":
        return <Badge variant="outline" className="bg-indigo-500/10 text-indigo-500">Frontend</Badge>;
      case "system":
        return <Badge variant="outline" className="bg-gray-500/10 text-gray-500">System</Badge>;
      case "certificate":
        return <Badge variant="outline" className="bg-green-500/10 text-green-500">Certificate</Badge>;
      default:
        return <Badge variant="outline">{source}</Badge>;
    }
  };

  const exportLogs = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      toast({
        title: "No logs to export",
        description: "There are no logs matching your current filters.",
        variant: "destructive"
      });
      return;
    }
    
    const headers = ["ID", "Timestamp", "Level", "Source", "Message", "Details", "Component", "Status"];
    const csvContent = [
      headers.join(","),
      ...filteredLogs.map(log => [
        log.id,
        new Date(log.timestamp).toISOString(),
        log.level,
        log.source,
        `"${log.message.replace(/"/g, '""')}"`,
        `"${(log.details || '').replace(/"/g, '""')}"`,
        log.component,
        log.status
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `logs-export-${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Logs exported",
      description: `Exported ${filteredLogs.length} logs to CSV.`,
    });
  };

  const handleCreateTestLog = async () => {
    if (!testLogMessage) {
      toast({
        title: "Message required",
        description: "Please enter a log message",
        variant: "destructive"
      });
      return;
    }
    
    createTestLogMutation.mutate({
      level: testLogLevel,
      source: testLogSource,
      message: testLogMessage,
      component: testLogComponent || undefined,
      details: testLogDetails || undefined
    });
  };

  return (
    <div className="container mx-auto pt-6 px-6 space-y-6 max-w-7xl animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-title font-bold tracking-tight text-primary">Technical Logs</h1>
          <p className="text-muted-foreground font-subtitle">Access system logs and diagnostics information</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setIsTestLoggerDialogOpen(true)}>
            <Zap className="h-4 w-4 text-amber-500" />
            Generate Test Log
          </Button>
          <Button variant="outline" className="gap-2" onClick={exportLogs}>
            <Download className="h-4 w-4" />
            Export Logs
          </Button>
          <Button className="gap-2" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {isLoadingCounts ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-sm border-l-4 border-l-red-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-section text-muted-foreground">Errors</p>
                <p className="text-2xl font-bold">{logCounts?.error || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500 opacity-80" />
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border-l-4 border-l-amber-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-section text-muted-foreground">Warnings</p>
                <p className="text-2xl font-bold">{logCounts?.warning || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-amber-500 opacity-80" />
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border-l-4 border-l-blue-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-section text-muted-foreground">Info</p>
                <p className="text-2xl font-bold">{logCounts?.info || 0}</p>
              </div>
              <Info className="h-10 w-10 text-blue-500 opacity-80" />
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border-l-4 border-l-gray-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-section text-muted-foreground">Debug</p>
                <p className="text-2xl font-bold">{logCounts?.debug || 0}</p>
              </div>
              <Bug className="h-10 w-10 text-gray-500 opacity-80" />
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="font-subtitle">
          <TabsTrigger value="application">Application</TabsTrigger>
          <TabsTrigger value="database">Database</TabsTrigger>
          <TabsTrigger value="auth">Authentication</TabsTrigger>
          <TabsTrigger value="api">API</TabsTrigger>
        </TabsList>
        
        {["application", "database", "auth", "api"].map(tab => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            <Card className="bg-white shadow">
              <CardHeader className="border-b border-accent-light/30 p-4">
                <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
                  <CardTitle className="text-xl font-bold font-title text-primary">
                    {tab.charAt(0).toUpperCase() + tab.slice(1)} Logs
                  </CardTitle>
                  <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search logs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 w-full md:w-[200px] font-subtitle"
                      />
                    </div>
                    <Select value={levelFilter} onValueChange={setLevelFilter}>
                      <SelectTrigger className="w-full md:w-[150px] font-subtitle">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Log level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="debug">Debug</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={sourceFilter} onValueChange={setSourceFilter}>
                      <SelectTrigger className="w-full md:w-[150px] font-subtitle">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="api">API</SelectItem>
                        <SelectItem value="database">Database</SelectItem>
                        <SelectItem value="auth">Auth</SelectItem>
                        <SelectItem value="frontend">Frontend</SelectItem>
                        <SelectItem value="system">System</SelectItem>
                        <SelectItem value="certificate">Certificate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-4 bg-accent-subtle/20 border-b border-accent-light/20">
                  <div className="flex items-center gap-2">
                    <Terminal className="h-4 w-4 text-primary" />
                    <span className="text-sm font-section text-muted-foreground">
                      {isLoadingLogs 
                        ? "Loading logs..." 
                        : `Showing ${filteredLogs.length} log entries`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 text-xs gap-1 text-primary"
                          disabled={!filteredLogs || filteredLogs.length === 0 || updateStatusMutation.isPending}
                        >
                          <Archive className="h-3.5 w-3.5" />
                          Archive Logs
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Archive Logs</DialogTitle>
                          <DialogDescription>
                            This will archive {filteredLogs?.length || 0} logs currently shown with your filters. 
                            Archived logs will no longer appear in the main view but can be retrieved if needed. 
                            Are you sure you want to continue?
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="mt-4">
                          <Button variant="outline" onClick={() => setIsArchiveDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleArchiveLogs}
                            disabled={updateStatusMutation.isPending}
                          >
                            {updateStatusMutation.isPending ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Archiving...
                              </>
                            ) : (
                              <>Archive Logs</>
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div className="divide-y divide-accent-light/20">
                  {isLoadingLogs ? (
                    <div className="p-4 space-y-4">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Skeleton key={i} className="h-24" />
                      ))}
                    </div>
                  ) : filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => (
                      <div key={log.id} className="p-4 hover:bg-accent-subtle/10">
                        <div className="flex flex-wrap gap-2 items-center mb-1">
                          <span className="font-mono text-xs">{log.id.substring(0, 8)}</span>
                          {getLevelBadge(log.level)}
                          {getSourceBadge(log.source)}
                          <Badge variant={
                            log.status === 'unresolved' ? 'destructive' : 
                            log.status === 'investigating' ? 'warning' : 
                            log.status === 'archived' ? 'outline' :
                            'success'
                          } className={
                            log.status === 'unresolved' ? 'bg-red-500' : 
                            log.status === 'investigating' ? 'bg-amber-500' : 
                            log.status === 'archived' ? 'bg-gray-500' :
                            'bg-green-500'
                          }>
                            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          </Badge>
                          <div className="flex items-center ml-auto">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                            <span className="text-xs text-muted-foreground font-caption">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="font-body text-sm mb-2">{log.message}</p>
                        {log.details && (
                          <pre className="bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto">{log.details}</pre>
                        )}
                        
                        <div className="flex justify-end mt-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 gap-1.5">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="text-xs font-section">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewDetails(log.id)} className="gap-1.5 text-xs font-section">
                                <Eye className="h-3.5 w-3.5" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(log.id, 'investigating')} 
                                className="gap-1.5 text-xs font-section"
                                disabled={log.status === 'investigating'}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Mark as Investigating
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(log.id, 'resolved')} 
                                className="gap-1.5 text-xs font-section text-green-600"
                                disabled={log.status === 'resolved'}
                              >
                                <CheckCircle className="h-3.5 w-3.5" />
                                Mark as Resolved
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(log.id, 'unresolved')} 
                                className="gap-1.5 text-xs font-section text-red-600"
                                disabled={log.status === 'unresolved'}
                              >
                                <AlertCircle className="h-3.5 w-3.5" />
                                Mark as Unresolved
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-muted-foreground">No logs found matching your filters</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          {getSelectedLog() && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span>Log Details</span>
                  {getSelectedLog() && getLevelBadge(getSelectedLog()!.level)}
                </DialogTitle>
                <DialogDescription>
                  ID: <span className="font-mono">{getSelectedLog()?.id}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <h3 className="text-sm font-medium mb-1">Status</h3>
                  <div className="flex gap-2">
                    <Badge variant={
                      getSelectedLog()?.status === 'unresolved' ? 'destructive' : 
                      getSelectedLog()?.status === 'investigating' ? 'warning' : 
                      getSelectedLog()?.status === 'archived' ? 'outline' :
                      'success'
                    }>
                      {getSelectedLog()?.status.charAt(0).toUpperCase() + getSelectedLog()?.status.slice(1)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-1">Message</h3>
                  <p className="bg-gray-50 p-3 rounded-md">{getSelectedLog()?.message}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-1">Source</h3>
                  <div className="flex gap-2">
                    {getSelectedLog() && getSourceBadge(getSelectedLog()!.source)}
                    <span className="text-sm">{getSelectedLog()?.component}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-1">Timestamp</h3>
                  <p className="text-sm text-muted-foreground">
                    {getSelectedLog() && new Date(getSelectedLog()!.timestamp).toLocaleString()}
                  </p>
                </div>
                {getSelectedLog()?.details && (
                  <div>
                    <h3 className="text-sm font-medium mb-1">Details</h3>
                    <pre className="bg-gray-100 p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                      {getSelectedLog()?.details}
                    </pre>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2 mt-4 sm:space-x-0">
                <div className="flex flex-wrap gap-2 w-full sm:justify-end">
                  <Button 
                    variant={getSelectedLog()?.status === 'investigating' ? 'secondary' : 'outline'} 
                    onClick={() => handleUpdateStatus(getSelectedLog()!.id, 'investigating')}
                    disabled={getSelectedLog()?.status === 'investigating'}
                    className="flex-1 sm:flex-none"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Mark Investigating
                  </Button>
                  <Button 
                    variant={getSelectedLog()?.status === 'resolved' ? 'secondary' : 'outline'} 
                    onClick={() => handleUpdateStatus(getSelectedLog()!.id, 'resolved')}
                    disabled={getSelectedLog()?.status === 'resolved'}
                    className="flex-1 sm:flex-none text-green-600"
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                    Mark Resolved
                  </Button>
                  <Button 
                    variant={getSelectedLog()?.status === 'unresolved' ? 'secondary' : 'outline'} 
                    onClick={() => handleUpdateStatus(getSelectedLog()!.id, 'unresolved')}
                    disabled={getSelectedLog()?.status === 'unresolved'}
                    className="flex-1 sm:flex-none text-red-600"
                  >
                    <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
                    Mark Unresolved
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isTestLoggerDialogOpen} onOpenChange={setIsTestLoggerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Test Log</DialogTitle>
            <DialogDescription>
              Generate a test log to verify the real-time logging system is working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Log Level</label>
                <Select value={testLogLevel} onValueChange={(val) => setTestLogLevel(val as LogLevel)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="debug">Debug</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Source</label>
                <Select value={testLogSource} onValueChange={(val) => setTestLogSource(val as LogSource)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="api">API</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="auth">Auth</SelectItem>
                    <SelectItem value="frontend">Frontend</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="certificate">Certificate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message</label>
              <Input 
                value={testLogMessage} 
                onChange={(e) => setTestLogMessage(e.target.value)} 
                placeholder="Log message"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Component (Optional)</label>
              <Input 
                value={testLogComponent} 
                onChange={(e) => setTestLogComponent(e.target.value)} 
                placeholder="Component name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Details (Optional)</label>
              <Input 
                value={testLogDetails} 
                onChange={(e) => setTestLogDetails(e.target.value)} 
                placeholder="Additional details"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsTestLoggerDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTestLog}
              disabled={createTestLogMutation.isPending || !testLogMessage}
            >
              {createTestLogMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>Create Log</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
